import { FC, useEffect, useState } from 'react'
import { Body, Dot, Dots, Kicker, TipWrapper } from './SecurityTip.styles'
import { Tip, TipPhase } from './SecurityTip.types'

const TIPS: Tip[] = [
    {
        id: 'asset-coverage',
        kicker: 'Tip',
        body: 'Check that all your domains and internet-facing assets are listed. Missing assets may mean missing risks.',
    },
    {
        id: 'issue-context',
        kicker: 'Tip',
        body: 'Open an issue to understand what was detected, why it matters, and how to fix it.',
    },
    {
        id: 'rescan-confirmation',
        kicker: 'Tip',
        body: 'After fixing an issue, allow time for the next scan to confirm that the exposure is no longer detected.',
    },
    {
        id: 'new-technologies',
        kicker: 'Tip',
        body: 'Review newly discovered technologies and services to identify anything unexpected or no longer in use.',
    },
    {
        id: 'cloud-apps',
        kicker: 'Tip',
        body: 'Connect your Cloud Apps to uncover user, configuration, and access risks that external scans cannot detect.',
    },
    {
        id: 'fraud-defense',
        kicker: 'Tip',
        body: 'Enable Fraud Defense to help protect employees from phishing, impersonation, and fraudulent emails.',
    },
]

const VISIBLE_MS = 3600
const TRANSITION_MS = 400

const SecurityTip: FC = () => {
    const [index, setIndex] = useState(0)
    const [phase, setPhase] = useState<TipPhase>('in')

    useEffect(() => {
        const outTimer = window.setTimeout(() => setPhase('out'), VISIBLE_MS)
        const advanceTimer = window.setTimeout(() => {
            setIndex((prev) => (prev + 1) % TIPS.length)
            setPhase('in')
        }, VISIBLE_MS + TRANSITION_MS)

        return () => {
            window.clearTimeout(outTimer)
            window.clearTimeout(advanceTimer)
        }
    }, [index])

    const { kicker, body } = TIPS[index]

    return (
        <TipWrapper phase={phase} aria-live="polite">
            <Kicker variant="xs-bold">{kicker}</Kicker>
            <Body variant="s-regular" noWrap={false} testId="security-tip-body">
                {body}
            </Body>
            <Dots role="tablist" aria-label="Security tips">
                {TIPS.map((tip, i) => (
                    <Dot key={tip.id} isActive={i === index} />
                ))}
            </Dots>
        </TipWrapper>
    )
}

export default SecurityTip
